import dayjs from 'dayjs';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { useGetCmsConfigsQuery } from '../../apis/cmsApi/cms.hooks.api';
import { isPdrOrder } from '../../helpers/mlkActions';
import {
  getCorrectStatus,
  getExpectedServiceDateAndTimeSlot,
} from '../../helpers/order';
import {
  Order,
  OrderState,
  BlockedReason,
  CalculatedOrderStatus,
} from '../../models';
import { useOrder } from '../useOrder';

export const useIsEditNotesEnabled = () => {
  const order = useOrder();
  const { data: cmsConfigs } = useGetCmsConfigsQuery(undefined);

  const isEditNotesAvailable = useMemo(() => {
    if (order && cmsConfigs) {
      const calculatedOrderStatus = getCorrectStatus(order);
      return getIsEditNotesAvailable(
        order,
        calculatedOrderStatus,
        cmsConfigs.featureFlags.isEditNotesEnabled
      );
    }
    return null;
  }, [cmsConfigs, order]);

  return isEditNotesAvailable;
};

const getIsEditNotesAvailable = (
  order: Order,
  calculatedOrderStatus: CalculatedOrderStatus | undefined,
  isServiceEnabled: boolean
) => {
  const isLastAttempt = order.blockedReasons?.includes(BlockedReason.B01);
  const isPdr = isPdrOrder(order);
  const isSuspended = calculatedOrderStatus === CalculatedOrderStatus.Suspended;
  const isValidOrderState =
    order.status === OrderState.InTransit ||
    order.status === OrderState.ToReschedule ||
    (order.status === OrderState.Blocked &&
      !isNil(order.blockedReasons) &&
      order.blockedReasons?.includes(BlockedReason.B04));

  const isValidCalculatedStatus =
    calculatedOrderStatus !== undefined &&
    (calculatedOrderStatus === CalculatedOrderStatus.TooSoon ||
      calculatedOrderStatus === CalculatedOrderStatus.Masked ||
      calculatedOrderStatus === CalculatedOrderStatus.Missed ||
      calculatedOrderStatus === CalculatedOrderStatus.ProtectionMode ||
      calculatedOrderStatus === CalculatedOrderStatus.StandBy ||
      calculatedOrderStatus === CalculatedOrderStatus.Scheduled ||
      calculatedOrderStatus === CalculatedOrderStatus.ScheduledReception);

  const expectedDateAndTimeSlot = getExpectedServiceDateAndTimeSlot(order);
  let isSameDayPdr = false;
  if (expectedDateAndTimeSlot) {
    const today = dayjs();
    const hoursDifference = expectedDateAndTimeSlot.to
      .hour(0)
      .minute(0)
      .second(0)
      .diff(today, 'hour');
    isSameDayPdr = hoursDifference <= 0 && !!isPdr;
  }

  return (
    !isLastAttempt &&
    !isSameDayPdr &&
    !isSuspended &&
    (isValidOrderState || isValidCalculatedStatus) &&
    isServiceEnabled
  );
};
